import { newId, type ModuleId, type Mutation } from '@tahi/core';
import {
  defaultTileId,
  defaultTodayDefs,
  nextPosition,
  positionForMove,
  TODAY,
  type TileRow,
} from '../../tiles/layout';
import { getTileDef, listTileDefs, type TileDef, type TileSize } from '../../tiles/registry';
import { makeMutation } from '../mutation';
import { listTiles } from '../repos/tiles';
import { getState, setState } from '../syncState';
import { runCommand, type CommandEnv } from './dispatch';

const SOURCE = 'system' as const;

function tileValues(def: TileDef, position: number) {
  return {
    dashboardId: TODAY,
    moduleId: def.moduleId,
    type: def.type,
    position,
    size: def.defaultSize,
    hidden: false,
    config: {},
  };
}

const update = (id: string, values: Record<string, unknown>) =>
  makeMutation('tiles', 'update', id, values, SOURCE);

export async function addTile(env: CommandEnv, type: string): Promise<string> {
  const def = getTileDef(type);
  if (!def) throw new Error(`Unknown tile type: ${type}`);
  const rows = await listTiles(env.db, TODAY);
  const id = newId();
  await runCommand(env, {
    mutations: [makeMutation('tiles', 'insert', id, tileValues(def, nextPosition(rows)), SOURCE)],
  });
  return id;
}

export async function removeTile(env: CommandEnv, id: string): Promise<void> {
  await runCommand(env, { mutations: [makeMutation('tiles', 'delete', id, undefined, SOURCE)] });
}

export async function setTileHidden(env: CommandEnv, id: string, hidden: boolean): Promise<void> {
  await runCommand(env, { mutations: [update(id, { hidden })] });
}

export async function resizeTile(env: CommandEnv, id: string, size: TileSize): Promise<void> {
  await runCommand(env, { mutations: [update(id, { size })] });
}

export async function setTileConfig(env: CommandEnv, id: string, config: unknown): Promise<void> {
  await runCommand(env, { mutations: [update(id, { config })] });
}

/** `list` is the order the user sees (edit mode includes hidden tiles). */
export async function moveTile(
  env: CommandEnv,
  list: readonly TileRow[],
  id: string,
  toIndex: number,
): Promise<void> {
  const position = positionForMove(list, id, toIndex);
  if (position === null) return;
  await runCommand(env, { mutations: [update(id, { position })] });
}

/** Default tiles for a module turned on for the first time (spec §6: its main tile goes on Today). */
export async function defaultTileMutationsFor(
  env: CommandEnv,
  moduleId: ModuleId,
): Promise<Mutation[]> {
  const rows = await listTiles(env.db, TODAY, { includeDeleted: true });
  let position = nextPosition(rows);
  const out: Mutation[] = [];
  for (const def of listTileDefs().filter((d) => d.moduleId === moduleId && d.defaultOnToday)) {
    const id = defaultTileId(env.userId, TODAY, def.type);
    if (rows.some((r) => r.id === id)) continue;
    out.push(makeMutation('tiles', 'insert', id, tileValues(def, position++), SOURCE));
  }
  return out;
}

/**
 * Puts every default tile back in default order and removes the rest. Data is never touched.
 * An insert on an existing default row restores it if it was removed, or resets it if not (§14).
 */
export async function resetToday(env: CommandEnv): Promise<void> {
  const rows = await listTiles(env.db, TODAY, { includeDeleted: true });
  const desired = defaultTodayDefs(env.usable);
  const desiredIds = new Set(desired.map((d) => defaultTileId(env.userId, TODAY, d.type)));
  const mutations: Mutation[] = [];
  for (const r of rows) {
    if (!r.deletedAt && !desiredIds.has(r.id))
      mutations.push(makeMutation('tiles', 'delete', r.id, undefined, SOURCE));
  }
  desired.forEach((def, i) => {
    const id = defaultTileId(env.userId, TODAY, def.type);
    mutations.push(makeMutation('tiles', 'insert', id, tileValues(def, i + 1), SOURCE));
  });
  await runCommand(env, { mutations });
}

/** First launch on an account: give Today its default layout. Never re-adds tiles the user removed. */
export async function ensureTodaySeeded(env: CommandEnv): Promise<void> {
  if (await getState(env.db, 'seeded_today')) return;
  const rows = await listTiles(env.db, TODAY, { includeDeleted: true });
  if (rows.length === 0) {
    const mutations = defaultTodayDefs(env.usable).map((def, i) =>
      makeMutation(
        'tiles',
        'insert',
        defaultTileId(env.userId, TODAY, def.type),
        tileValues(def, i + 1),
        SOURCE,
      ),
    );
    await runCommand(env, { mutations });
  }
  await setState(env.db, 'seeded_today', new Date().toISOString());
}
